import {
  Box,
  Button,
  Content,
  Divider,
  Flex,
  fromTheme,
  Grid,
  Radio,
  StrokeIcon,
  usePlatformInfo,
  usePlatformProps,
} from '@acsbe/ui';
import axios from 'axios';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useBilling } from '../../../context';
import { BillingMethod } from '../../../context/BillingContext';
import { OneRowLoadingState } from '../../../ui/LoadingState';
import { AddPaymentMethodForm } from './add-payment-method';
import { BillingCardCompact } from './billing-card-compact';
import { ChipByState } from './billing-chip-by-state';
import { InvalidIconButton } from './billling-invalid-icon-button';
import { getPaymentMethodState } from './common';

type ChoosePaymentMethodSectionProps = {
  onBillingChange: (billingId: string) => void;
  selectedBillingId: string;
  setPaymentError?: (msg: string) => void;
};

export const ChoosePaymentMethodSection: FunctionComponent<
  ChoosePaymentMethodSectionProps
> = ({ onBillingChange, selectedBillingId, setPaymentError }) => {
  const { t } = useTranslation();
  const p = usePlatformProps();
  const { uptoPhablet } = usePlatformInfo();
  const { billingMethods, setBillingMethods, isProcessingPayment } =
    useBilling();
  const billingMethodsArray: BillingMethod[] = Array.isArray(billingMethods)
    ? billingMethods
    : [];
  const [filterBillings, setFilterBillings] =
    useState<BillingMethod[]>(billingMethodsArray);
  const [isNewPaymentMethod, setIsNewPaymentMethod] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isLoading = false;

  const handleBillingMethodSelection = (billing: BillingMethod) => {
    // Only call onBillingChange if the card is not disabled
    if (
      !billing.invalid &&
      !billing.isExpiredCard &&
      !billing.isZipInvalidByCountry
    ) {
      onBillingChange?.(billing._id);
    }
  };

  useEffect(() => {
    setFilterBillings(Array.isArray(billingMethods) ? billingMethods : []);
    setIsNewPaymentMethod(false);
  }, [billingMethods]);

  useEffect(() => {
    if (filterBillings) {
      // Find a valid (non-expired, non-invalid) billing method
      const validBilling = filterBillings.find(
        (billing: BillingMethod) =>
          !billing.invalid &&
          !billing.isExpiredCard &&
          !billing.isZipInvalidByCountry,
      );

      // Check if current selection exists
      const currentSelectedBilling = filterBillings.find(
        (billing: BillingMethod) => selectedBillingId === billing._id,
      );

      // If current selection doesn't exist or is invalid, set to a valid one
      if (
        !currentSelectedBilling ||
        currentSelectedBilling.invalid ||
        currentSelectedBilling.isExpiredCard ||
        currentSelectedBilling.isZipInvalidByCountry
      ) {
        onBillingChange(validBilling?._id || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBillings]);

  const removeBillingMethod = async (billingIdToRemove: string) => {
    setIsDeleting(true);
    try {
      await axios.delete(`/api/stripe/remove/${billingIdToRemove}`, {
        withCredentials: true,
      });
      setBillingMethods((prev) =>
        prev?.filter(
          (billing: BillingMethod) => billing._id !== billingIdToRemove,
        ),
      );
    } catch (error) {
      if (setPaymentError) {
        setPaymentError('Failed to remove payment method. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const ModifiedPaymentForm = () => {
    return (
      <Flex align="flex-start">
        <Flex paddingInline={6} paddingBlockStart={6}>
          <Button
            size="lg"
            variant="link"
            scale="primary"
            data-mixpanel="back-payment-form"
            type="button"
            onClick={() => setIsNewPaymentMethod(false)}
          >
            <StrokeIcon name="ArrowLeft" />
            <span>{t('BACK')}</span>
          </Button>
        </Flex>
        <AddPaymentMethodForm />
      </Flex>
    );
  };

  return (
    <Flex gap={3} as="section">
      {!isNewPaymentMethod && (
        <Grid padding={4} radius="lg" as="ul">
          {isLoading ? (
            <OneRowLoadingState />
          ) : (
            filterBillings?.length === 0 && (
              <Content size="xl" variant="element">
                {t('COMPLETE_PURCHASE_MODAL.NO_BILLING_YET')}
              </Content>
            )
          )}
          {
            //@ts-ignore
            filterBillings.map((billing, index, items) => {
              const {
                cardIsInvalidOrZipInvalid,
                canUpdateZipCodeFromBillingCard,
              } = getPaymentMethodState(billing);

              return (
                <Flex key={billing._id} as="li" data-test-item-id={billing._id}>
                  <CardContainer
                    row
                    gap={6}
                    align="center"
                    radius="lg"
                    {...p.fromTablet({ paddingBlock: 2, paddingInline: 3 })}
                    {...p.uptoPhablet({
                      gap: 3,
                    })}
                  >
                    <Radio
                      name="billingId"
                      disabled={
                        billing.invalid ||
                        billing.isExpiredCard ||
                        billing.isZipInvalidByCountry
                      }
                      checked={
                        billing._id === selectedBillingId ||
                        filterBillings.length === 1
                      }
                      value={billing._id}
                      onChange={() => handleBillingMethodSelection(billing)}
                    />

                    <Flex
                      row
                      width="100%"
                      align="end"
                      justify="space-between"
                      gap={6}
                      wrap="wrap"
                      {...p.uptoPhablet({ gap: 2 })}
                      {...p.uptoMobile({ row: false, align: 'start' })}
                    >
                      <Flex
                        row
                        align="center"
                        gap={6}
                        stretch
                        minWidth="0px"
                        {...p.uptoPhablet({ gap: 2 })}
                      >
                        <BillingCardCompact
                          card={billing}
                          disabled={cardIsInvalidOrZipInvalid}
                        >
                          {canUpdateZipCodeFromBillingCard ? (
                            <InvalidIconButton card={billing} size="md" />
                          ) : null}
                        </BillingCardCompact>

                        <ChipByState card={billing} />
                      </Flex>
                      {filterBillings?.length! > 1 && (
                        <Button
                          disabled={isDeleting}
                          variant="tertiary"
                          aria-label="Remove Billing Method"
                          data-mixpanel="remove-billing-method"
                          onClick={() => removeBillingMethod(billing._id)}
                        >
                          <StrokeIcon name="Trash" />
                        </Button>
                      )}
                    </Flex>
                  </CardContainer>

                  {index === items.length - 1 ? null : (
                    <Box marginBlock={2}>
                      <Divider />
                    </Box>
                  )}
                </Flex>
              );
            })
          }
          <Divider />
        </Grid>
      )}
      {isNewPaymentMethod ? (
        <ModifiedPaymentForm />
      ) : (
        <Flex
          row
          gap={5}
          paddingInline={4}
          paddingBlockEnd={6}
          align="center"
          justify="space-between"
        >
          <Button
            type="button"
            disabled={isProcessingPayment}
            scale="primary"
            variant="link"
            data-mixpanel="add-payment-method-choose-component"
            onClick={() => setIsNewPaymentMethod(true)}
          >
            <span>
              {uptoPhablet
                ? t('BILLING_PAGE.ADD')
                : t('BILLING_PAGE.ADD_NEW_PAYMENT_METHOD')}
            </span>
            <StrokeIcon name="ChevronRight" />
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

const CardContainer = styled(Flex).attrs({ as: 'label' })`
  &:not(:has(:disabled)) {
    cursor: pointer;

    &:hover {
      background-color: ${fromTheme('palette.scales.base.border.static')};
    }

    &:active {
      background-color: ${fromTheme(
        'palette.scales.primary.background.static',
      )};
    }
  }
`;
